"use client";

import type { Rating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type React from "react";
import type {
  CareSeekerLeadFormResponse,
  CareSeekerShallowLeadFormData,
  LeadSource,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { HeroIconVariant } from "~www/types/national-hero";
import type { CareOptionType } from "./shared/form-options";
import type { HeroVariant } from "./shared/hero-variants";

import {
  identifyLead,
  ServiceLeadFormSubmitEvent,
  track,
  trackLeadCreated,
} from "@homeinstead/analytics";
import { careSeekerContractSchema } from "@homeinstead/forms/schemas";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useActionState, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { submitCareSeekerShallowForm } from "~www/actions/submitCareSeekerShallowForm";
import { useHandleFormSubmit } from "~www/hooks/useHandleFormSubmit";
import { getCareSeekerDefaultValues } from "./get-care-seeker-default-values";
import OfficeStep from "./OfficeStep";
import CareOptionStep from "./shared/CareOptionStep";
import { ContactStep } from "./shared/ContactStep";
import { getHeroVariantStyles } from "./shared/hero-variants";
import ThankYou from "./shared/ThankYou";
import CareRecipientStep from "./v1/CareRecipientStep";

const ERROR_CONTEXT = "CareSeekerNationalLeadForm";
const SUBMISSION_ERROR = "Submission Error";

interface CareSeekerNationalLeadFormProps
  extends React.FormHTMLAttributes<HTMLFormElement> {
  currentStep: number;
  handleBack: () => void;
  handleNext: () => void;
  finalStep: number;
  isDebug?: boolean;

  // From sanity
  heroHeading: PortableTextBlock[];
  iconVariant?: HeroIconVariant;
  placeholders?: Record<string, string>;
  leadSource: LeadSource;
  phoneNumber: string;

  rating?: Rating;
  variant?: HeroVariant;
}

const CareSeekerNationalLeadForm = ({
  className,
  currentStep,
  handleBack,
  handleNext,
  finalStep,
  isDebug,
  heroHeading,
  iconVariant,
  placeholders,
  leadSource,
  rating,
  variant = "light",
  phoneNumber,
  ...props
}: CareSeekerNationalLeadFormProps) => {
  const { textColorClass } = getHeroVariantStyles(variant);

  const methods = useForm<CareSeekerShallowLeadFormData>({
    defaultValues: getCareSeekerDefaultValues(),
    mode: "onSubmit",
    resolver: zodResolver(careSeekerContractSchema),
    shouldUnregister: false,
  });

  const { getValues } = methods;

  const [state, submitFormAction, isPending] = useActionState(
    submitCareSeekerShallowForm,
    null,
  ) as [
    LeadResponse<CareSeekerLeadFormResponse> | null,
    typeof submitFormAction,
    boolean,
  ];

  const handleFormSubmit =
    useHandleFormSubmit<CareSeekerShallowLeadFormData>(methods);

  // make sure these only fire once
  const isSuccessTracked = useRef(false);
  const isErrorTracked = useRef(false);
  const isValidationErrorTracked = useRef(false);

  const [
    careFor,
    careType,
    organizationNumber,
    firstName,
    lastName,
    email,
    phone,
  ] = getValues([
    "careFor",
    "careType",
    "organizationNumber",
    "firstName",
    "lastName",
    "email",
    "phone",
  ]);

  const { memoryCare, everydayTasks, mobilityAssistance, other } = careType;
  const careOptions: CareOptionType[] = [
    memoryCare && "memoryCare",
    everydayTasks && "everydayTasks",
    mobilityAssistance && "mobilityAssistance",
    other && "other",
  ].filter(Boolean) as CareOptionType[];

  /**
   * If form has been successfully submitted,
   * handle server side validations,
   * and submit to Segment
   */
  // TODO stop suppressing this warning (don't replicate this!)
  /* eslint-disable react-hooks/refs -- fire-once analytics guard during post-submit render */
  if (state?.ok && !state?.data?.isError) {
    const leadId = state.data?.id;

    // Only track success once
    if (!isSuccessTracked.current && leadId) {
      isSuccessTracked.current = true;

      if (isDebug) {
        console.log("Successful submission, tracking in Segment");
        console.table([
          {
            Email: email,
            "First Name": firstName,
            "Franchise Number": organizationNumber,
            "Last Name": lastName,
            "Lead ID": leadId,
            Phone: phone,
          },
        ]);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          formVariant: "v1",
          franchiseNumber: organizationNumber.toString(),
          recipient: careFor,
          result: "success",
          selectedOptions: careOptions,
          tags: state.tags,
        }),
      );

      identifyLead({
        email,
        firstName,
        lastName,
        phone,
      });

      // Track lead creation - its own event for consistency with the other platform
      // (otherwise we could add lead and organization to the "service-lead-form-submit")
      trackLeadCreated({
        formVariant: "v1",
        leadId,
        organizationNumber: organizationNumber || undefined,
        recipient: careFor,
        selectedOptions: careOptions,
        tags: state.tags,
      });
    }

    return <ThankYou firstName={firstName} variant={variant} />;
  } else if (state?.ok && state?.data?.isError) {
    const errors = state?.data?.errors || [];

    const isDuplicateError = errors.some(
      (error: { code: string }) => error.code === "duplicate",
    );

    if (!isErrorTracked.current) {
      isErrorTracked.current = true;
      const errorResult = isDuplicateError ? "duplicate" : "server-error";

      if (!isDuplicateError) {
        console.error({
          error: errors,
          message: `${ERROR_CONTEXT} - ${SUBMISSION_ERROR}: ${errorResult}`,
        });
      }

      if (isDebug) {
        console.log("Error submission, tracking in Segment: ", errorResult);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          formVariant: "v1",
          franchiseNumber: organizationNumber.toString(),
          recipient: careFor,
          result: errorResult,
          selectedOptions: careOptions,
          tags: state.tags,
        }),
      );
    }

    // We show thank you to duplicates (spam or otherwise) to throw them off, but we don't let
    // them proceed in the process with creating a lead (in SF).
    return <ThankYou firstName={getValues("firstName")} variant={variant} />;
  } else if (state !== null && !state?.ok) {
    // Only track errors if we've actually attempted a submission
    const validationErrors = state?.validationErrors;

    if (!isValidationErrorTracked.current) {
      isValidationErrorTracked.current = true;
      const errorResult = validationErrors
        ? "validation-error"
        : "server-error";

      if (isDebug) {
        console.log("Error submission, tracking in Segment: ", errorResult);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          formVariant: "v1",
          franchiseNumber: organizationNumber.toString(),
          recipient: careFor,
          result: errorResult,
          selectedOptions: careOptions,
          tags: state.tags,
          validationErrors,
        }),
      );
    }
  }
  /* eslint-enable react-hooks/refs */

  /**
   * Otherwise render form
   * Note: At the moment, some Steps are rendered in a Suspense boundary because each Step calls useSearchParams
   * via useSetQueryParam hook. This is a Next.js requirement.
   */
  return (
    <FormProvider {...methods}>
      <form
        action={submitFormAction}
        className={cn(className, "care-seeker-national-lead-form")}
        onSubmit={handleFormSubmit}
        {...props}
      >
        <Stepper
          className="mx-auto w-full md:w-[440px] xl:ml-0"
          currentStep={currentStep - 1}
          handleBack={handleBack}
          showBackButton={currentStep > 2}
          textColorClass={textColorClass}
          totalSteps={finalStep - 2}
        />
        <input name="leadSource" type="hidden" value={leadSource} />
        <input name="formVariant" type="hidden" value="v1" />

        {/**
         * Step 2 - who needs care?
         */}
        <CareRecipientStep
          className={currentStep === 2 ? "flex" : "hidden"}
          formVariant="v1"
          heroHeading={heroHeading}
          iconVariant={iconVariant}
          nextStep={handleNext}
          officeRating={rating}
          phoneNumber={phoneNumber}
          placeholders={placeholders}
          variant={variant}
        />
        {/**
         * Step 3 - kinds of assistance needed
         */}
        <CareOptionStep
          className={currentStep === 3 ? "flex" : "hidden"}
          ctaVariant="primary"
          formVariant="v1"
          nextStep={handleNext}
          variant={variant}
        />
        {/**
         * Step 4 - Where is care needed
         */}
        <OfficeStep
          className={currentStep === 4 ? "flex" : "hidden"}
          formVariant="v1"
          nextStep={handleNext}
          variant={variant}
        />

        {/**
         * Step 5 - Contact information
         */}
        <ContactStep
          className={currentStep === 5 ? "flex" : "hidden"}
          isPending={isPending}
          variant={variant}
        />
      </form>
    </FormProvider>
  );
};

export default CareSeekerNationalLeadForm;
